import React from 'react';

import Section, { SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Coverage
 *
 * Proof of scale, ported from the storefront's "A decade of protection"
 * section — the heritage framing that explains why the numbers are large
 * despite the network having almost no online footprint, the numbers
 * themselves, and the installed base drawn on a map.
 *
 * The copy is re-aimed. The storefront is selling water sensors to anyone in a
 * serviceable area, so its version says "now available to everyone, order it
 * directly". A replicated website is selling the whole network through one
 * local dealership, so the point is the opposite: this has always been sold
 * and installed in person, and the dealership reading this page is one of the
 * hundred-odd companies that does it.
 *
 * NOTE ON THE FIGURES: these are the network-wide numbers carried on the
 * storefront (73K homes, 969K sensors, 130+ partners). They are the same
 * network, so they hold here — but they are not this dealership's numbers and
 * the page is careful to say so. Confirm them before launch; they are stated
 * as fact on a real company's website.
 */

const Coverage = ({ dealership }) => {

    const mobile = Utils.isMobile();

    const stats = [
        { label: 'Homes protected', value: '73K+' },
        { label: 'Sensors installed', value: '969K+' },
        { label: 'Dealers nationwide', value: '130+' }
    ];

    return (
        <Section id={'coverage'} tinted={true}>
            <SectionHeader
            eyebrow={'A decade of protection'}
            subtitle={`This network has never been sold off a shelf. For over a decade it has been surveyed, installed and explained in person, one home at a time, by authorized dealers across North America, and ${dealership?.name || 'your local dealer'} is one of them. Every point on the map below is a home where somebody walked the rooms first and worked out what that particular house needed.`}
            title={'Hundreds of thousands of rooms, already covered'} />

            <div style={{
                display: 'grid',
                gap: mobile ? 22 : 40,
                gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)',
                ...mobile && {
                    backgroundColor: Theme.colors.white,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 20,
                    boxShadow: Theme.boxShadow.card,
                    padding: '30px 24px'
                }
            }}>
                {stats.map((stat, index) => (
                    <div
                    key={index}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        position: 'relative'
                    }}>
                        {index < stats.length - 1 && mobile === false && (
                            <span style={{
                                color: Theme.colors.primary(),
                                fontSize: 24,
                                fontWeight: 300,
                                opacity: 0.5,
                                position: 'absolute',
                                right: -24,
                                top: '30%'
                            }}>{'+'}</span>
                        )}
                        <span style={Theme.textStyles.stat()}>{stat.value}</span>
                        <span style={Theme.textStyles.caption({ fontSize: 14 })}>{stat.label}</span>
                    </div>
                ))}
            </div>

            <div style={{ marginTop: mobile ? 32 : 72 }}>
                <div style={{
                    backgroundColor: Theme.colors.lighterGrey,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 16,
                    boxShadow: Theme.boxShadow.card,
                    overflow: 'hidden',
                    paddingTop: '50%',
                    position: 'relative',
                    width: '100%'
                }}>
                    <img
                    alt={'Map of protected homes across North America'}
                    src={'images/coverage-map.png'}
                    style={{
                        height: '100%',
                        left: 0,
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        width: '100%'
                    }} />
                </div>
                <span style={{
                    ...Theme.textStyles.caption({ color: Theme.colors.subText(), textAlign: 'center' }),
                    display: 'block',
                    marginTop: 16
                }}>{'Every point is a home already protected by the network.'}</span>
            </div>
        </Section>
    )
}

export default Coverage;
